import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import ErrorBanner from '../ErrorBanner';
import PmIntakeFields from '../service/PmIntakeFields';
import RichTextEditor from '../richText/RichTextEditor';
import { appendRichText } from '../richText/richText';
import { flattenRichText, isRichTextBlank } from '../../../../shared/richTextFlatten.js';
import {
  createExternalServiceTicket,
  fetchClientServiceAgreements,
  fetchServiceSettings,
  generateExternalServiceTicketTitle,
  normalizeServiceTicketTimePreference,
  selectClientServiceAgreementForTicket,
  serviceKeys,
  serviceSettingsFromQueryData,
  serviceTicketStartTimeForPreference,
  SERVICE_TICKET_TIME_PREFERENCE_OPTIONS,
  updateServiceTicket,
  type ClientServiceAgreementRow,
  type ServiceTicketPriority,
  type ServiceTicketTimePreference,
} from '../../data/service';
import {
  clientContactDisplayName,
  createClientBranchQuick,
  createClientContactQuick,
  createClientQuick,
  fetchClientBasics,
  fetchClientBranchDetails,
  fetchClientContactOptions,
  fetchClientSearchResults,
  type ClientBasicRecord,
  type ClientBranchDetail,
  type ClientContactOption,
  type ClientSummary,
} from '../../data/clients';
import {
  fetchLaborCalendarWindow,
  laborCalendarKeys,
  type LaborCalendarFilters,
  type LaborScheduleShift,
} from '../../data/laborCalendar';
import { fetchServiceTechnicianOptions, type UserOption } from '../../data/userProfiles';
import { useRoles } from '../../hooks/useRoles';
import useSessionStore from '../../state/session';
import {
  canCreateExternalServiceTicketForLocation,
  SERVICE_LOCATION_ACCESS_TOOLTIP,
} from '../../utils/serviceLocationAccess';
import { runSupabaseQuery, safeErrorMessage } from '../../utils/appErrors';
import { createInstallationJob, type InstallationLocationKind } from '../../data/installations';
import { searchVenues, type VenueOption } from '../../data/eventWizard';
import { fetchVenueDetail } from '../../data/venues';
import './CustomerWorkIntakeForm.css';


function errorMessage(err: unknown, fallback: string): string {
  return safeErrorMessage(err, fallback);
}

function intakeSaveErrorMessage(err: unknown, isInstallation: boolean): string {
  const message = err && typeof err === 'object' && 'message' in err ? String(err.message) : '';
  if (isInstallation && message.startsWith('No active doc sequence found')) {
    return 'Installation job numbering is not configured. Ask an administrator to configure numbering for the Installation location, then retry.';
  }
  return errorMessage(err, isInstallation ? 'Installation quote could not be created. Try again.' : 'Failed to create external service ticket');
}

type QuickCreateMode = 'customer' | 'contact' | 'location' | null;
type ExternalSubmitStep = 'ticket' | 'open';

type QuickCustomerDraft = {
  businessName: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
};

type QuickContactDraft = {
  firstName: string;
  lastName: string;
  title: string;
  email: string;
  phone: string;
};

type QuickLocationDraft = {
  branchName: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
};

type SpecificServiceItemDraft = {
  description: string;
  make: string;
  model: string;
  age: string;
};

const emptyCustomerDraft = (): QuickCustomerDraft => ({
  businessName: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  postalCode: '',
  country: 'US',
});

const emptyContactDraft = (): QuickContactDraft => ({
  firstName: '',
  lastName: '',
  title: '',
  email: '',
  phone: '',
});

const emptyLocationDraft = (): QuickLocationDraft => ({
  branchName: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  postalCode: '',
  country: 'US',
});

const emptySpecificServiceItemDraft = (): SpecificServiceItemDraft => ({
  description: '',
  make: '',
  model: '',
  age: '',
});

const SERVICE_WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function trimOrNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function todayYmd(): string {
  return toYmd(new Date());
}

function parseYmd(value?: string | null): Date {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ''));
  if (!match) return new Date();
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return new Date();
  return new Date(year, month - 1, day, 12, 0, 0, 0);
}

function toYmd(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function startOfServiceWeek(value?: string | null): Date {
  const date = parseYmd(value);
  const offset = (date.getDay() + 6) % 7;
  return addDays(date, -offset);
}

function serviceWeekDays(value?: string | null): Date[] {
  const start = startOfServiceWeek(value);
  return Array.from({ length: 7 }, (_, index) => addDays(start, index));
}

function serviceWeekRange(value?: string | null): { startYmd: string; endYmd: string; windowStart: string; windowEnd: string } {
  const start = startOfServiceWeek(value);
  const end = addDays(start, 6);
  const exclusiveEnd = addDays(start, 7);
  return {
    startYmd: toYmd(start),
    endYmd: toYmd(end),
    windowStart: new Date(start.getFullYear(), start.getMonth(), start.getDate(), 0, 0, 0, 0).toISOString(),
    windowEnd: new Date(exclusiveEnd.getFullYear(), exclusiveEnd.getMonth(), exclusiveEnd.getDate(), 0, 0, 0, 0).toISOString(),
  };
}

function formatServiceWeekRange(value?: string | null): string {
  const { startYmd, endYmd } = serviceWeekRange(value);
  const start = parseYmd(startYmd);
  const end = parseYmd(endYmd);
  const startLabel = start.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  const endLabel = end.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  return `${startLabel} - ${endLabel}`;
}

function formatServiceDayHeader(date: Date, index: number): string {
  return `${SERVICE_WEEKDAY_LABELS[index]} ${date.getDate()}`;
}

function ymdFromIso(value?: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return '';
  return toYmd(date);
}

function timeFromIso(value?: string | null): string {
  if (!value) return '09:00';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return '09:00';
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

function formatServiceClock(value?: string | null): string {
  const time = String(value || '').slice(0, 5);
  const [hourText, minuteText] = time.split(':');
  const hour = Number(hourText);
  const minute = Number(minuteText);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return '--';
  return new Date(2000, 0, 1, hour, minute).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

function formatScheduleShiftTime(shift: LaborScheduleShift): string {
  const start = formatServiceClock(timeFromIso(shift.startsAt));
  const end = shift.endsAt ? formatServiceClock(timeFromIso(shift.endsAt)) : null;
  if (!end || end === '--') return start;
  return `${start} - ${end}`;
}

function scheduleShiftProfileIds(shift: LaborScheduleShift): string[] {
  return shift.assignments
    .filter((assignment) => assignment.assignmentType === 'employee' && assignment.profileId)
    .filter((assignment) => !['cancelled', 'declined', 'withdrawn'].includes(assignment.status))
    .map((assignment) => assignment.profileId as string);
}

function scheduleShiftSummary(shift: LaborScheduleShift): string {
  return [shift.eventCode, shift.eventName, shift.roleName].filter(Boolean).join(' - ') || 'Scheduled work';
}

function specificItemLines(enabled: boolean, item: SpecificServiceItemDraft): string[] {
  if (!enabled) return [];
  return [
    ['Description', item.description],
    ['Make', item.make],
    ['Model', item.model],
    ['Age', item.age],
  ]
    .map(([label, value]) => [label, String(value || '').trim()] as const)
    .filter(([, value]) => Boolean(value))
    .map(([label, value]) => `- ${label}: ${value}`);
}

function buildExternalProblemReported(problem: string, itemEnabled: boolean, item: SpecificServiceItemDraft): string {
  const itemLines = specificItemLines(itemEnabled, item);
  if (!itemLines.length) return problem;
  return appendRichText(problem, `Specific item:\n${itemLines.join('\n')}`);
}

function locationCityLine(city?: string | null, state?: string | null, postalCode?: string | null): string {
  return [city, state, postalCode].map((value) => value?.trim()).filter(Boolean).join(', ');
}

function clientMainLocation(client: ClientBasicRecord | null, summary: ClientSummary | null): QuickLocationDraft {
  return {
    branchName: summary?.businessName || client?.business_name || 'Customer main location',
    addressLine1: client?.address_line1 || '',
    addressLine2: client?.address_line2 || '',
    city: client?.city || summary?.city || '',
    state: client?.state || summary?.state || '',
    postalCode: client?.postal_code || '',
    country: client?.country || '',
  };
}

function branchServiceLocation(branch: ClientBranchDetail): QuickLocationDraft {
  return {
    branchName: branch.branch_name || 'Service location',
    addressLine1: branch.shipping_address_line1 || branch.billing_address_line1 || '',
    addressLine2: branch.shipping_address_line2 || branch.billing_address_line2 || '',
    city: branch.shipping_city || branch.billing_city || '',
    state: branch.shipping_state || branch.billing_state || '',
    postalCode: branch.shipping_postal_code || branch.billing_postal_code || '',
    country: branch.shipping_country || branch.billing_country || '',
  };
}

function addressLines(location: QuickLocationDraft): string[] {
  return [
    location.addressLine1,
    location.addressLine2,
    locationCityLine(location.city, location.state, location.postalCode),
    location.country,
  ].map((value) => value.trim()).filter(Boolean);
}

function formatAddressInline(location: QuickLocationDraft): string {
  return addressLines(location).join(', ');
}

function formatLocationOption(location: QuickLocationDraft, fallbackLabel?: string): string {
  const label = location.branchName || fallbackLabel || 'Customer location';
  const address = formatAddressInline(location);
  return address ? `${label} - ${address}` : label;
}

function findActiveAgreement(agreements: ClientServiceAgreementRow[], branchId: string): ClientServiceAgreementRow | null {
  return selectClientServiceAgreementForTicket(agreements, { serviceDate: new Date().toISOString().slice(0, 10), clientBranchId: branchId || null });
}

function agreementLabel(agreement: ClientServiceAgreementRow | null): string {
  if (!agreement) return 'No active service agreement';
  const tier = agreement.tier?.name || 'Service agreement';
  return `${tier} (${agreement.start_date} to ${agreement.end_date})`;
}

function contactOptionLabel(contact: ClientContactOption): string {
  const name = clientContactDisplayName(contact);
  const details = [contact.title, contact.email, contact.phone].filter(Boolean).join(' / ');
  return details ? `${name} - ${details}` : name;
}

function contactMatchesSearch(contact: ClientContactOption, search: string): boolean {
  const term = search.trim().toLowerCase();
  if (!term) return true;
  return [
    clientContactDisplayName(contact),
    contact.title,
    contact.email,
    contact.phone,
  ].filter(Boolean).join(' ').toLowerCase().includes(term);
}

function serviceContactValidationMessage(contactSearch: string, isInstallation = false): string {
  const record = isInstallation ? 'quote' : 'ticket';
  return contactSearch.trim()
    ? `Choose a contact from the list, or use + Contact to add this person before creating the ${record}.`
    : `Select a ${isInstallation ? 'client' : 'service'} contact before creating the ${record}.`;
}

function fallbackExternalTicketTitle(problemReported: string): string {
  const text = problemReported.replace(/\s+/g, ' ').trim();
  if (!text) return 'External Service Request';
  const sentence = text.split(/[.!?]/)[0]?.trim() || text;
  return sentence.length > 80 ? `${sentence.slice(0, 77).trim()}...` : sentence;
}

function externalSubmitStepLabel(step: ExternalSubmitStep | null): string {
  if (step === 'ticket') return 'Creating the external service ticket...';
  if (step === 'open') return 'Opening the new ticket...';
  return 'Creating the external service ticket...';
}

function CustomerSearchResult({
  client,
  expanded,
  disabled,
  onSelect,
  onToggleDetails,
}: {
  client: ClientSummary;
  expanded: boolean;
  disabled: boolean;
  onSelect: () => void;
  onToggleDetails: () => void;
}) {
  const location = locationCityLine(client.city, client.state, null);
  return (
    <div className="stack gap-xxs" style={{ padding: '0.55rem 0', borderTop: '1px solid var(--border-subtle, #d8e2ed)' }}>
      <div className="flex between center gap-sm wrap">
        <button
          key={client.id}
          type="button"
          className="btn subtle"
          style={{ justifyContent: 'flex-start', flex: '1 1 260px' }}
          onClick={onSelect}
          disabled={disabled}
        >
          {client.businessName || 'Untitled customer'}
          {location ? ` - ${location}` : ''}
        </button>
        <button type="button" className="btn sm" onClick={onToggleDetails} disabled={disabled}>
          {expanded ? 'Hide Details' : 'Details'}
        </button>
      </div>
      {expanded ? (
        <div className="responsive-grid text-xs fg-muted" style={{ '--min-col': '160px' } as React.CSSProperties}>
          <span>Customer ID: {client.id}</span>
          <span>Location: {location || '--'}</span>
          <span>Tax status: {client.taxStatus || '--'}</span>
          <Link to={`/clients/${client.id}/info`} className="link">Open customer record</Link>
        </div>
      ) : null}
    </div>
  );
}

function ExternalServiceSchedulePreview({
  weekAnchor,
  requestedDate,
  requestedStartTime,
  serviceLocationName,
  serviceLocationId,
  serviceLocationLoading,
  people,
  peopleLoading,
  peopleError,
  shifts,
  shiftsLoading,
  shiftsError,
  onPreviousWeek,
  onNextWeek,
  onToday,
  onSelectSlot,
}: {
  weekAnchor: string;
  requestedDate: string;
  requestedStartTime: string;
  serviceLocationName: string;
  serviceLocationId: string | null;
  serviceLocationLoading: boolean;
  people: UserOption[];
  peopleLoading: boolean;
  peopleError: string | null;
  shifts: LaborScheduleShift[];
  shiftsLoading: boolean;
  shiftsError: string | null;
  onPreviousWeek: () => void;
  onNextWeek: () => void;
  onToday: () => void;
  onSelectSlot: (date: string, time: string) => void;
}) {
  const days = React.useMemo(() => serviceWeekDays(weekAnchor), [weekAnchor]);
  const dayKeys = React.useMemo(() => new Set(days.map(toYmd)), [days]);
  const peopleById = React.useMemo(() => new Map(people.map((person) => [person.id, person])), [people]);
  const shiftsByRowDay = React.useMemo(() => {
    const map = new Map<string, LaborScheduleShift[]>();
    const addShift = (rowId: string, dayKey: string, shift: LaborScheduleShift) => {
      const key = `${rowId}:${dayKey}`;
      const existing = map.get(key) ?? [];
      existing.push(shift);
      map.set(key, existing);
    };
    for (const shift of shifts) {
      const dayKey = ymdFromIso(shift.startsAt);
      if (!dayKeys.has(dayKey)) continue;
      const profileIds = scheduleShiftProfileIds(shift).filter((profileId) => peopleById.has(profileId));
      if (shift.isOpen) {
        addShift('open', dayKey, shift);
      } else if (!profileIds.length) {
        addShift('unassigned', dayKey, shift);
      }
      for (const profileId of profileIds) addShift(`person:${profileId}`, dayKey, shift);
    }
    for (const rows of map.values()) {
      rows.sort((a, b) => a.startsAt.localeCompare(b.startsAt));
    }
    return map;
  }, [dayKeys, peopleById, shifts]);
  const rowDefs = React.useMemo(() => [
    { id: 'open', label: 'Open shifts', detail: '' },
    { id: 'unassigned', label: 'Unassigned', detail: '' },
    ...people.map((person) => ({ id: `person:${person.id}`, label: person.label, detail: 'Service role' })),
  ], [people]);
  const selectedLabel = requestedDate
    ? `${parseYmd(requestedDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} at ${formatServiceClock(requestedStartTime)}`
    : 'No exact date selected';
  const shiftCount = shifts.length;

  return (
    <div className="service-schedule-preview" aria-label="Service team schedule preview">
      <div className="service-schedule-preview__toolbar">
        <div className="service-schedule-preview__buttons">
          <button type="button" className="btn sm" onClick={onPreviousWeek} aria-label="Previous week">Prev</button>
          <button type="button" className="btn sm" onClick={onToday}>Today</button>
          <button type="button" className="btn sm" onClick={onNextWeek} aria-label="Next week">Next</button>
        </div>
        <div className="service-schedule-preview__title">
          <strong>{formatServiceWeekRange(weekAnchor)}</strong>
          <span>{serviceLocationName}{shiftCount ? ` - ${shiftCount} scheduled` : ''}</span>
        </div>
        <span className="service-schedule-preview__selected">{selectedLabel}</span>
      </div>

      {!serviceLocationId && !serviceLocationLoading ? (
        <div className="service-schedule-preview__notice" role="alert">Service location is not configured, so the team schedule cannot load.</div>
      ) : null}
      {peopleError ? <div className="service-schedule-preview__notice" role="alert">{peopleError}</div> : null}
      {shiftsError ? <div className="service-schedule-preview__notice" role="alert">{shiftsError}</div> : null}
      {serviceLocationLoading || peopleLoading || shiftsLoading ? <div className="service-schedule-preview__notice">Loading service schedule...</div> : null}

      <div className="service-schedule-preview__scroller">
        <div className="service-schedule-grid">
          <div className="service-schedule-grid__corner">People</div>
          {days.map((day, index) => {
            const dayKey = toYmd(day);
            return (
              <button
                key={dayKey}
                type="button"
                className={`service-schedule-grid__day ${requestedDate === dayKey ? 'is-selected' : ''}`}
                onClick={() => onSelectSlot(dayKey, requestedStartTime)}
              >
                <span>{formatServiceDayHeader(day, index)}</span>
                <small>{day.toLocaleDateString(undefined, { month: 'short' })}</small>
              </button>
            );
          })}

          {rowDefs.map((row) => (
            <React.Fragment key={row.id}>
              <div className="service-schedule-grid__person">
                <strong>{row.label}</strong>
                {row.detail ? <span>{row.detail}</span> : null}
              </div>
              {days.map((day) => {
                const dayKey = toYmd(day);
                const cellShifts = shiftsByRowDay.get(`${row.id}:${dayKey}`) ?? [];
                return (
                  <div
                    key={`${row.id}:${dayKey}`}
                    className={`service-schedule-grid__cell ${requestedDate === dayKey ? 'is-selected' : ''}`}
                  >
                    {cellShifts.length ? cellShifts.map((shift) => {
                      const shiftTime = timeFromIso(shift.startsAt);
                      return (
                        <button
                          key={`${shift.id}:${shift.startsAt}`}
                          type="button"
                          className="service-schedule-shift"
                          onClick={() => onSelectSlot(dayKey, shiftTime)}
                        >
                          <strong>{formatScheduleShiftTime(shift)}</strong>
                          <span>{scheduleShiftSummary(shift)}</span>
                        </button>
                      );
                    }) : (
                      <span className="service-schedule-grid__empty">-</span>
                    )}
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}

type CustomerWorkIntakeFormProps = {
  installation?: { onCancel: () => void; onCreated?: (jobId: string) => void };
};

export default function CustomerWorkIntakeForm({ installation }: CustomerWorkIntakeFormProps) {
  const isInstallation = Boolean(installation);
  const [installationName, setInstallationName] = React.useState('');
  const [installationQuoteType, setInstallationQuoteType] = React.useState<'installation' | 'service_agreement'>('installation');
  const [venueSearch, setVenueSearch] = React.useState('');
  const [selectedVenue, setSelectedVenue] = React.useState<VenueOption | null>(null);
  const [customAddress, setCustomAddress] = React.useState(() => ({ ...emptyLocationDraft(), branchName: 'Other address' }));
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const userId = useSessionStore((s) => s.session?.user?.id ?? null);
  const allowedLocationIds = useSessionStore((s) => s.allowedLocationIds ?? []);
  const { canEditPage, canEditFeature, isAdmin, isManager } = useRoles();
  const canAccessService = canEditPage('service') && canEditFeature('service.tickets', 'own');
  // Customer › Service's "Quote service agreement" and Re-quote open this intake with the type and customer chosen.
  const linkedServiceAgreement = isInstallation && searchParams.get('quoteType') === 'service_agreement';
  const initialClientId = isInstallation ? (linkedServiceAgreement ? searchParams.get('clientId') || '' : '') : searchParams.get('clientId') || '';
  React.useEffect(() => { if (linkedServiceAgreement) setInstallationQuoteType('service_agreement'); }, [linkedServiceAgreement]);
  const initialPmVisitId = searchParams.get('pmVisitId') || '';
  const [pmSelection, setPmSelection] = React.useState(initialPmVisitId || 'none');
  const [creationId] = React.useState(() => crypto.randomUUID());
  const [clientSearch, setClientSearch] = React.useState('');
  const [debouncedClientSearch, setDebouncedClientSearch] = React.useState('');
  const [expandedClientId, setExpandedClientId] = React.useState<string | null>(null);
  const [selectedClient, setSelectedClient] = React.useState<ClientSummary | null>(
    initialClientId ? { id: initialClientId, businessName: 'Selected customer', city: null, state: null, taxStatus: null } : null,
  );
  const [branchId, setBranchId] = React.useState(isInstallation ? '' : searchParams.get('branchId') || '');
  const [room, setRoom] = React.useState('');
  const [selectedContactId, setSelectedContactId] = React.useState('');
  const [contactSearch, setContactSearch] = React.useState('');
  const [contactOpen, setContactOpen] = React.useState(false);
  const [requestedDate, setRequestedDate] = React.useState(isInstallation ? '' : searchParams.get('requestedDate') || '');
  const [requestedTimePreference, setRequestedTimePreference] = React.useState<ServiceTicketTimePreference>('anytime');
  const [requestedStartTime, setRequestedStartTime] = React.useState('09:00');
  const [problem, setProblem] = React.useState('');
  const [specificItemEnabled, setSpecificItemEnabled] = React.useState(false);
  const [specificItem, setSpecificItem] = React.useState(emptySpecificServiceItemDraft);
  const [scheduleWeekAnchor, setScheduleWeekAnchor] = React.useState(() => todayYmd());
  const [priority, setPriority] = React.useState<ServiceTicketPriority>('normal');
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [submitAttempted, setSubmitAttempted] = React.useState(false);
  const [submitStep, setSubmitStep] = React.useState<ExternalSubmitStep | null>(null);
  const [quickMode, setQuickMode] = React.useState<QuickCreateMode>(null);
  const [quickSaving, setQuickSaving] = React.useState(false);
  const [quickError, setQuickError] = React.useState<string | null>(null);
  const [quickCustomer, setQuickCustomer] = React.useState(emptyCustomerDraft);
  const [quickContact, setQuickContact] = React.useState(emptyContactDraft);
  const [quickLocation, setQuickLocation] = React.useState(emptyLocationDraft);

  React.useEffect(() => {
    const timer = setTimeout(() => setDebouncedClientSearch(clientSearch), 250);
    return () => clearTimeout(timer);
  }, [clientSearch]);

  React.useEffect(() => {
    setSelectedContactId('');
    setContactSearch('');
    setContactOpen(false);
    setBranchId(!isInstallation && selectedClient?.id === initialClientId ? searchParams.get('branchId') || '' : '');
    setPmSelection(!isInstallation && selectedClient?.id === initialClientId ? initialPmVisitId || 'none' : 'none');
    setRoom('');
    setSubmitAttempted(false);
    setSelectedVenue(null);
    setVenueSearch('');
    setCustomAddress({ ...emptyLocationDraft(), branchName: 'Other address' });
  }, [selectedClient?.id]);

  const clientSearchQuery = useQuery({
    queryKey: ['service-ticket-client-search', debouncedClientSearch],
    queryFn: () => runSupabaseQuery(
      () => fetchClientSearchResults(debouncedClientSearch),
      'Unable to search customers.',
      'clients.search',
    ),
    enabled: debouncedClientSearch.trim().length >= 2,
    staleTime: 60_000,
  });

  const clientBasicsQuery = useQuery({
    queryKey: ['service-ticket-client-basics', selectedClient?.id],
    queryFn: () => runSupabaseQuery(
      () => fetchClientBasics(selectedClient?.id || ''),
      'Unable to load customer details.',
      'clients.basics',
    ),
    enabled: Boolean(selectedClient?.id),
    staleTime: 60_000,
  });

  const branchesQuery = useQuery({
    queryKey: ['service-ticket-client-branches', selectedClient?.id],
    queryFn: () => runSupabaseQuery(
      () => fetchClientBranchDetails(selectedClient?.id || ''),
      'Unable to load customer locations.',
      'clients.branches',
    ),
    enabled: Boolean(selectedClient?.id),
    staleTime: 60_000,
  });

  const contactsQuery = useQuery({
    queryKey: ['service-ticket-client-contacts', selectedClient?.id],
    queryFn: () => runSupabaseQuery(
      () => fetchClientContactOptions(selectedClient?.id || ''),
      'Unable to load customer contacts.',
      'clients.contacts',
    ),
    enabled: Boolean(selectedClient?.id),
    staleTime: 60_000,
  });

  const agreementsQuery = useQuery({
    queryKey: selectedClient?.id ? serviceKeys.clientAgreements(selectedClient.id) : ['service-tickets', 'client-agreements', 'none'],
    queryFn: () => runSupabaseQuery(
      () => fetchClientServiceAgreements(selectedClient?.id || ''),
      'Unable to load the customer service agreement.',
      'service.clientAgreements',
    ),
    enabled: !isInstallation && Boolean(selectedClient?.id),
    staleTime: 60_000,
  });

  const serviceSettingsQuery = useQuery({
    queryKey: serviceKeys.settings(),
    queryFn: () => runSupabaseQuery(
      () => fetchServiceSettings(),
      'Unable to load service settings.',
      'service.settings',
    ),
    enabled: !isInstallation,
    staleTime: 60_000,
  });

  const branches = React.useMemo(() => branchesQuery.data?.branches ?? [], [branchesQuery.data?.branches]);
  const contacts = React.useMemo(() => contactsQuery.data ?? [], [contactsQuery.data]);
  const agreements = React.useMemo(() => agreementsQuery.data ?? [], [agreementsQuery.data]);
  const serviceSettings = serviceSettingsFromQueryData(serviceSettingsQuery.data);
  const serviceLocationId = serviceSettings?.serviceLocationId ?? null;
  const serviceLocationName = serviceSettings?.serviceLocationName ?? 'Service location';
  const canCreateExternalTicket = canCreateExternalServiceTicketForLocation({
    canAccessService,
    isAdmin,
    isManager,
    serviceLocationId,
    allowedLocationIds,
  });
  const externalTicketDisabledTitle = canCreateExternalTicket ? undefined : SERVICE_LOCATION_ACCESS_TOOLTIP;
  const activeAgreement = React.useMemo(() => findActiveAgreement(agreements, branchId), [agreements, branchId]);
  const selectedBranch = React.useMemo(() => branches.find((branch) => String(branch.id) === branchId) ?? null, [branches, branchId]);
  const selectedContact = React.useMemo(() => contacts.find((contact) => contact.id === selectedContactId) ?? null, [contacts, selectedContactId]);
  const contactValidationMessage = React.useMemo(() => (
    selectedClient && !selectedContact ? serviceContactValidationMessage(contactSearch, isInstallation) : null
  ), [contactSearch, isInstallation, selectedClient, selectedContact]);
  const showContactValidation = Boolean(contactValidationMessage && (submitAttempted || contactSearch.trim()));
  const selectedClientBasics = clientBasicsQuery.data ?? null;
  const venueSearchQuery = useQuery({
    queryKey: ['installation-create-venues', venueSearch],
    queryFn: () => runSupabaseQuery(() => searchVenues(venueSearch.trim()), 'Unable to search venues.', 'installations.venues'),
    enabled: isInstallation && branchId === 'venue',
  });
  const venueDetailQuery = useQuery({
    queryKey: ['installation-create-venue-detail', selectedVenue?.id],
    queryFn: () => runSupabaseQuery(() => fetchVenueDetail(selectedVenue!.id), 'Unable to load the venue address.', 'installations.venue'),
    enabled: isInstallation && branchId === 'venue' && Boolean(selectedVenue),
  });
  const venue = venueDetailQuery.data;
  const locationDraft = isInstallation && branchId === 'other'
    ? customAddress
    : isInstallation && branchId === 'venue'
      ? { branchName: venue?.venue_name || selectedVenue?.venue_name || 'Selected venue', addressLine1: venue?.address_line1 || '', addressLine2: venue?.address_line2 || '', city: venue?.city || '', state: venue?.state || '', postalCode: venue?.postal_code || '', country: venue?.country || '' }
      : selectedBranch ? branchServiceLocation(selectedBranch) : clientMainLocation(selectedClientBasics, selectedClient);
  const locationError = clientBasicsQuery.error || branchesQuery.error || (branchId === 'venue' ? venueDetailQuery.error : null);
  const addressReady = Boolean(locationDraft.addressLine1.trim() && locationDraft.city.trim() && locationDraft.state.trim());
  const installationReady = Boolean(installationName.trim() && selectedClient && selectedContact && addressReady && !locationError
    && !clientBasicsQuery.isFetching && !branchesQuery.isFetching && !contactsQuery.isFetching
    && (!/^\d+$/.test(branchId) || selectedBranch)
    && (branchId !== 'venue' || (selectedVenue && !venueDetailQuery.isFetching)));
  const locationLines = addressLines(locationDraft);
  const filteredContacts = React.useMemo(() => {
    const rows = contacts.filter((contact) => contactMatchesSearch(contact, selectedContactId ? '' : contactSearch));
    return rows.sort((a, b) => {
      const aMatchesBranch = branchId && a.branchIds.includes(branchId) ? 0 : 1;
      const bMatchesBranch = branchId && b.branchIds.includes(branchId) ? 0 : 1;
      if (aMatchesBranch !== bMatchesBranch) return aMatchesBranch - bMatchesBranch;
      return clientContactDisplayName(a).localeCompare(clientContactDisplayName(b));
    });
  }, [branchId, contactSearch, contacts, selectedContactId]);

  React.useEffect(() => {
    if (isInstallation || !activeAgreement?.client_branch_id || branchId) return;
    setBranchId(String(activeAgreement.client_branch_id));
  }, [activeAgreement?.client_branch_id, branchId, isInstallation]);

  React.useEffect(() => {
    if (!requestedDate) return;
    setScheduleWeekAnchor(requestedDate);
  }, [requestedDate]);

  const scheduleWeek = React.useMemo(() => serviceWeekRange(scheduleWeekAnchor), [scheduleWeekAnchor]);
  const scheduleEnabled = !isInstallation && requestedTimePreference === 'exact';
  const serviceLaborUsersQuery = useQuery({
    queryKey: ['service-technician-options', serviceLocationId],
    queryFn: () => runSupabaseQuery(
      () => fetchServiceTechnicianOptions(serviceLocationId),
      'Unable to load service users.',
      'service.laborUsers',
    ),
    enabled: scheduleEnabled && Boolean(serviceLocationId),
    staleTime: 5 * 60_000,
  });
  const scheduleFilters = React.useMemo<LaborCalendarFilters>(() => ({
    windowStart: scheduleWeek.windowStart,
    windowEnd: scheduleWeek.windowEnd,
    scope: 'team',
    locationIds: serviceLocationId ? [serviceLocationId] : [],
    publishStatus: 'all',
    assignmentStatus: 'all',
    myEventsOnly: false,
    includeUnconfirmedEvents: true,
  }), [scheduleWeek.windowEnd, scheduleWeek.windowStart, serviceLocationId]);
  const serviceScheduleQuery = useQuery({
    queryKey: laborCalendarKeys.window(scheduleFilters),
    queryFn: () => runSupabaseQuery(
      () => fetchLaborCalendarWindow(scheduleFilters, {
        hydrateEventDetails: false,
        filterLogisticsRequirement: false,
      }),
      'Unable to load the service schedule.',
      'service.schedule',
    ),
    enabled: scheduleEnabled && Boolean(serviceLocationId),
    staleTime: 30_000,
  });

  function selectClient(client: ClientSummary) {
    setSelectedClient(client);
    setClientSearch('');
    setDebouncedClientSearch('');
    setExpandedClientId(null);
  }

  function openQuickModal(mode: QuickCreateMode) {
    setQuickMode(mode);
    setQuickError(null);
    if (mode === 'customer') setQuickCustomer({ ...emptyCustomerDraft(), businessName: clientSearch.trim() });
    if (mode === 'contact') setQuickContact(emptyContactDraft());
    if (mode === 'location') setQuickLocation(emptyLocationDraft());
  }

  async function handleQuickCustomerSubmit(event: React.FormEvent) {
    event.preventDefault();
    setQuickSaving(true);
    setQuickError(null);
    try {
      const { data, error: createError } = await createClientQuick({
        businessName: quickCustomer.businessName,
        addressLine1: trimOrNull(quickCustomer.addressLine1),
        addressLine2: trimOrNull(quickCustomer.addressLine2),
        city: trimOrNull(quickCustomer.city),
        state: trimOrNull(quickCustomer.state),
        postalCode: trimOrNull(quickCustomer.postalCode),
        country: trimOrNull(quickCustomer.country),
      });
      if (createError || !data) throw createError || new Error('Customer name is required.');
      selectClient(data);
      setQuickMode(null);
      await queryClient.invalidateQueries({ queryKey: ['service-ticket-client-search'] });
    } catch (err: unknown) {
      setQuickError(errorMessage(err, 'Failed to create customer'));
    } finally {
      setQuickSaving(false);
    }
  }

  async function handleQuickLocationSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!selectedClient?.id) return;
    setQuickSaving(true);
    setQuickError(null);
    try {
      const { data, error: createError } = await createClientBranchQuick({
        clientId: selectedClient.id,
        branchName: quickLocation.branchName,
        addressLine1: trimOrNull(quickLocation.addressLine1),
        addressLine2: trimOrNull(quickLocation.addressLine2),
        city: trimOrNull(quickLocation.city),
        state: trimOrNull(quickLocation.state),
        postalCode: trimOrNull(quickLocation.postalCode),
        country: trimOrNull(quickLocation.country),
      });
      if (createError || !data) throw createError || new Error('Service location name is required.');
      setBranchId(data.id);
      setQuickMode(null);
      await queryClient.invalidateQueries({ queryKey: ['service-ticket-client-branches', selectedClient.id] });
      await branchesQuery.refetch();
    } catch (err: unknown) {
      setQuickError(errorMessage(err, 'Failed to create service location'));
    } finally {
      setQuickSaving(false);
    }
  }

  async function handleQuickContactSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!selectedClient?.id) return;
    setQuickSaving(true);
    setQuickError(null);
    try {
      const { data, error: createError } = await createClientContactQuick({
        clientId: selectedClient.id,
        firstName: trimOrNull(quickContact.firstName),
        lastName: trimOrNull(quickContact.lastName),
        title: trimOrNull(quickContact.title),
        email: trimOrNull(quickContact.email),
        phone: trimOrNull(quickContact.phone),
        branchId: /^\d+$/.test(branchId) ? branchId : null,
      });
      if (createError || !data) throw createError || new Error('At least one contact field is required.');
      setSelectedContactId(data.id);
      setContactSearch(contactOptionLabel(data));
      setContactOpen(false);
      setSubmitAttempted(false);
      setError(null);
      setQuickMode(null);
      await queryClient.invalidateQueries({ queryKey: ['service-ticket-client-contacts', selectedClient.id] });
      await contactsQuery.refetch();
    } catch (err: unknown) {
      setQuickError(errorMessage(err, 'Failed to create contact'));
    } finally {
      setQuickSaving(false);
    }
  }

  function handleContactInput(value: string) {
    setContactSearch(value);
    setContactOpen(true);
    if (selectedContact && value !== contactOptionLabel(selectedContact)) {
      setSelectedContactId('');
    }
  }

  function handleRequestedTimePreferenceChange(value: string) {
    const preference = normalizeServiceTicketTimePreference(value);
    setRequestedTimePreference(preference);
    setRequestedStartTime((current) => serviceTicketStartTimeForPreference(preference, current));
    if (preference === 'exact' && !requestedDate) {
      const today = todayYmd();
      setRequestedDate(today);
      setScheduleWeekAnchor(today);
    }
  }

  function handleRequestedStartTimeChange(value: string) {
    setRequestedTimePreference('exact');
    setRequestedStartTime(value || '09:00');
  }

  function handleRequestedDateChange(value: string) {
    setRequestedDate(value);
    if (value) setScheduleWeekAnchor(value);
  }

  function handleScheduleSlotSelect(date: string, time: string) {
    setRequestedDate(date);
    setScheduleWeekAnchor(date);
    setRequestedTimePreference('exact');
    setRequestedStartTime(time || '09:00');
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (saving || quickSaving) return;
    setSubmitAttempted(true);
    const problemReported = flattenRichText(problem).trim();
    if (!selectedClient?.id) {
      setError(`Select a customer before creating the ${isInstallation ? 'quote' : 'ticket'}.`);
      return;
    }
    if (!selectedContact) {
      setError(serviceContactValidationMessage(contactSearch, isInstallation));
      setContactOpen(true);
      return;
    }
    if (!isInstallation && isRichTextBlank(problem)) {
      setError('Enter the problem reported before creating the ticket.');
      return;
    }
    if (!isInstallation && !canCreateExternalTicket) {
      setError(SERVICE_LOCATION_ACCESS_TOOLTIP);
      return;
    }
    if (isInstallation && !installationReady) {
      setError('Enter an installation name and choose a site with a street, city, and state before creating the quote.');
      return;
    }
    setSaving(true);
    setError(null);
    setSubmitStep('ticket');
    try {
      if (installation) {
        const locationKind: InstallationLocationKind = branchId === 'venue' ? 'venue' : branchId === 'other' ? 'other' : branchId ? 'branch' : 'client';
        const jobId = await createInstallationJob({
          quote_type: installationQuoteType,
          name: installationName.trim(), client_id: selectedClient.id, primary_contact_id: selectedContact.id,
          client_branch_id: locationKind === 'branch' ? Number(branchId) : null,
          venue_id: locationKind === 'venue' ? selectedVenue?.id ?? null : null,
          installation_location_kind: locationKind, requested_start_date: requestedDate || null,
          installation_address_line1: locationDraft.addressLine1.trim(), installation_address_line2: locationDraft.addressLine2.trim(),
          installation_city: locationDraft.city.trim(), installation_state: locationDraft.state.trim(),
          installation_postal_code: locationDraft.postalCode.trim(), installation_country: locationDraft.country.trim(),
        });
        installation.onCreated?.(jobId);
        return;
      }
      const fallbackTitle = fallbackExternalTicketTitle(problemReported);
      const fullProblemReported = buildExternalProblemReported(problem, specificItemEnabled, specificItem);

      const { data, error: createError, defaultLinesIncomplete, tripChargeCheck } = await createExternalServiceTicket({
        clientId: selectedClient.id,
        clientBranchId: branchId || activeAgreement?.client_branch_id || null,
        serviceRoom: room,
        serviceContactName: clientContactDisplayName(selectedContact),
        serviceContactEmail: selectedContact.email,
        serviceContactPhone: selectedContact.phone,
        serviceAgreementAssignmentId: activeAgreement?.assignment_id ?? null,
        serviceAgreementTierId: activeAgreement?.tier_id ?? null,
        pmVisitId: ['none', 'paid'].includes(pmSelection) ? null : pmSelection,
        isPmVisit: pmSelection !== 'none',
        creationId,
        summary: fallbackTitle,
        problemReported: fullProblemReported,
        requestedServiceDate: requestedDate || null,
        requestedServiceTimePreference: requestedTimePreference,
        requestedServiceStartTime: serviceTicketStartTimeForPreference(requestedTimePreference, requestedStartTime),
        priority,
        openedBy: userId,
        actorUserId: userId,
      });
      if (createError || !data) throw createError || new Error('Failed to create external service ticket');
      const ticketId = data.service_ticket_id;
      void generateExternalServiceTicketTitle({
        customerName: selectedClient.businessName,
        serviceLocationName: locationDraft.branchName,
        serviceRoom: room,
        serviceAddress: formatAddressInline(locationDraft),
        contactName: clientContactDisplayName(selectedContact),
        priority,
        requestedServiceDate: requestedDate || null,
        problemReported: flattenRichText(fullProblemReported),
      }).then(async (generatedTitle) => {
        if (generatedTitle.error) {
          console.warn('[service-ticket-create] title generation failed after ticket creation', generatedTitle.error);
          return;
        }
        const generatedSummary = generatedTitle.data?.trim();
        if (!generatedSummary || generatedSummary === fallbackTitle) return;
        const updateResult = await updateServiceTicket(ticketId, {
          summary: generatedSummary,
          updated_by: userId,
        });
        if (updateResult.error) {
          console.warn('[service-ticket-create] title update failed after ticket creation', updateResult.error);
        }
      }).catch((titleError) => {
        console.warn('[service-ticket-create] title generation failed after ticket creation', titleError);
      });
      setSubmitStep('open');
      navigate(`/service/${data.service_ticket_id}`, {
        state: { defaultLinesIncomplete: Boolean(defaultLinesIncomplete), tripChargeCheck: tripChargeCheck ?? null },
      });
    } catch (err: unknown) {
      setError(intakeSaveErrorMessage(err, isInstallation));
    } finally {
      setSaving(false);
      setSubmitStep(null);
    }
  }

  const quickModalTitle = quickMode === 'customer'
    ? 'Add Customer'
    : quickMode === 'contact'
      ? 'Add Contact'
      : isInstallation ? 'Add Location' : 'Add Service Location';
  const quickFormId = quickMode ? `service-quick-${quickMode}-form` : undefined;
  const quickSubmitLabel = quickMode === 'customer'
    ? (quickSaving ? 'Adding...' : 'Add Customer')
    : quickMode === 'contact'
      ? (quickSaving ? 'Adding...' : 'Add Contact')
      : (quickSaving ? 'Adding...' : isInstallation ? 'Add Location' : 'Add Service Location');
  const quickSubmitDisabled = quickSaving
    || (quickMode === 'customer' && !quickCustomer.businessName.trim())
    || (quickMode === 'location' && !quickLocation.branchName.trim())
    || (quickMode === 'contact' && ![quickContact.firstName, quickContact.lastName, quickContact.email, quickContact.phone].some((value) => value.trim()));
  const submitFeedbackMessage = error || (saving ? (isInstallation ? 'Creating the installation quote...' : externalSubmitStepLabel(submitStep)) : null);


  const actions = (
          <div className="service-intake-actions">
            <button type="button" className="btn" onClick={() => installation ? installation.onCancel() : navigate('/service')} disabled={saving}>
              Cancel
            </button>
            <span className="service-location-locked-action" title={isInstallation ? undefined : externalTicketDisabledTitle}>
              <button
                type="submit"
                form={isInstallation ? "installation-create-form" : "customer-work-intake-form"}
                className="btn btn-primary"
                disabled={saving || !selectedClient || (isInstallation ? !installationReady : !canCreateExternalTicket)}
                title={isInstallation ? undefined : externalTicketDisabledTitle}
              >
                {saving ? 'Creating...' : isInstallation ? 'Create quote' : 'Create External Ticket'}
              </button>
            </span>
          </div>
  );
  const intake = (
      <form id={isInstallation ? "installation-create-form" : "customer-work-intake-form"} className="settings-content service-create-form" onSubmit={handleSubmit} aria-busy={saving}>
        {error ? <ErrorBanner message={error} /> : null}

        <section className="section-shell service-intake-shell">
          <div className="service-intake-header">
            <div className="stack gap-xxs">
              <h2 className="h4 m0">{isInstallation ? 'Installation Intake' : 'External Service Intake'}</h2>
            </div>
          </div>

          <div className="service-intake-grid">
            <div className="service-intake-column">
              <div className="service-intake-column__header">
                <h3 className="h5 m0">Customer</h3>
                <button type="button" className="btn sm" onClick={() => openQuickModal('customer')} disabled={saving}>
                  + Customer
                </button>
              </div>

              <label className="stack gap-xxs">
                <span className="text-xxs fg-muted">Search customer</span>
                <input
                  className="input"
                  value={clientSearch}
                  onChange={(event) => setClientSearch(event.target.value)}
                  placeholder="Type at least 2 characters"
                  disabled={saving}
                />
              </label>
              {clientSearchQuery.isFetching ? <span className="text-xxs fg-muted">Searching...</span> : null}
              {clientSearchQuery.data?.length ? (
                <div className="service-search-results stack">
                  {clientSearchQuery.data.map((client) => (
                    <CustomerSearchResult
                      key={client.id}
                      client={client}
                      expanded={expandedClientId === client.id}
                      disabled={saving}
                      onSelect={() => selectClient(client)}
                      onToggleDetails={() => setExpandedClientId((current) => (current === client.id ? null : client.id))}
                    />
                  ))}
                </div>
              ) : null}
              {clientSearchQuery.error ? (
                <span className="text-xs fg-error" role="alert">{errorMessage(clientSearchQuery.error, 'Unable to search customers.')}</span>
              ) : null}
              {clientSearchQuery.isSuccess && debouncedClientSearch.trim().length >= 2 && !clientSearchQuery.data.length ? (
                <span className="text-xs fg-muted">No matching customers.</span>
              ) : null}

              <div className="service-readonly-field">
                <span className="text-xxs fg-muted">Selected customer</span>
                {selectedClient ? (
                  <>
                    <span className="text-sm fw-semibold">{selectedClientBasics?.business_name || selectedClient.businessName || selectedClient.id}</span>
                    <span className="text-xs fg-muted">
                      {locationCityLine(selectedClientBasics?.city || selectedClient.city, selectedClientBasics?.state || selectedClient.state, selectedClientBasics?.postal_code) || 'Location not set'}
                    </span>
                  </>
                ) : (
                  <span className="text-sm fg-muted">Search and select a customer to load locations and contacts.</span>
                )}
              </div>

              <div className="service-readonly-field service-readonly-field--compact">
                <span className="text-xxs fg-muted">Customer details</span>
                {selectedClient ? (
                  <>
                    <span className="text-sm">Tax: {selectedClientBasics?.tax_status || selectedClient.taxStatus || '--'}</span>
                    <Link to={`/clients/${selectedClient.id}/info`} className="link text-xs">Open customer record</Link>
                  </>
                ) : (
                  <span className="text-sm fg-muted">No customer selected</span>
                )}
              </div>

              {!isInstallation ? <PmIntakeFields clientId={selectedClient?.id || ''} branchId={branchId} date={requestedDate} agreements={agreements} value={pmSelection} onChange={setPmSelection} /> : null}
              {!isInstallation ? <div className="service-readonly-field service-readonly-field--compact">
                <span className="text-xxs fg-muted">Service agreement</span>
                <span className="text-sm">{selectedClient ? (agreementsQuery.isFetching ? 'Loading agreement...' : agreementLabel(activeAgreement)) : 'Select a customer first'}</span>
              </div> : null}
            </div>

            <div className="service-intake-column">
              <div className="service-intake-column__header">
                <h3 className="h5 m0">Location & Contact</h3>
                <div className="flex gap-xs wrap">
                  <button type="button" className="btn sm" onClick={() => openQuickModal('location')} disabled={saving || !selectedClient?.id}>
                    + Location
                  </button>
                  <button type="button" className="btn sm" onClick={() => openQuickModal('contact')} disabled={saving || !selectedClient?.id}>
                    + Contact
                  </button>
                </div>
              </div>

              <label className="stack gap-xxs">
                <span className="text-xxs fg-muted">Customer location</span>
                <select className="input service-location-select" value={branchId} onChange={(event) => setBranchId(event.target.value)} disabled={saving || !selectedClient}>
                  <option value="">
                    {selectedClient ? formatLocationOption(clientMainLocation(selectedClientBasics, selectedClient), 'Customer main location') : 'Select a customer first'}
                  </option>
                  {branches.map((branch) => {
                    const branchLocation = branchServiceLocation(branch);
                    return (
                      <option key={branch.id} value={branch.id}>
                        {formatLocationOption(branchLocation)}
                      </option>
                    );
                  })}
                  {isInstallation ? <><option value="venue">Venue address</option><option value="other">Other address</option></> : null}
                </select>
              </label>

              {!isInstallation ? <label className="stack gap-xxs">
                <span className="text-xxs fg-muted">Room</span>
                <input
                  className="input"
                  value={room}
                  onChange={(event) => setRoom(event.target.value)}
                  placeholder="Room, suite, floor, or area"
                  disabled={saving || !selectedClient}
                />
              </label> : null}

              {isInstallation && branchId === 'venue' ? (
                <div className="stack gap-xs">
                  <label className="stack gap-xxs"><span className="text-xxs fg-muted">Find venue *</span><input className="input" type="search" value={venueSearch} onChange={event => { setVenueSearch(event.target.value); setSelectedVenue(null); }} disabled={saving} placeholder="Search venue name" /></label>
                  <div className="service-search-results stack" role="listbox" aria-label="Venue results">
                    {(venueSearchQuery.data ?? []).map(option => <button key={option.id} type="button" className="btn subtle" role="option" aria-selected={selectedVenue?.id === option.id} disabled={saving} onClick={() => { setSelectedVenue(option); setVenueSearch(option.venue_name); }}>{option.venue_name}</button>)}
                  </div>
                  {venueSearchQuery.error ? <ErrorBanner message={errorMessage(venueSearchQuery.error, 'Unable to search venues.')} /> : null}
                </div>
              ) : null}
              {isInstallation && branchId === 'other' ? (
                <div className="service-intake-address-grid">
                  {(['addressLine1', 'addressLine2', 'city', 'state', 'postalCode', 'country'] as const).map(field => {
                    const labels = { addressLine1: 'Address line 1', addressLine2: 'Address line 2', city: 'City', state: 'State', postalCode: 'Postal code', country: 'Country' };
                    const required = ['addressLine1', 'city', 'state'].includes(field);
                    return <label key={field} className="stack gap-xxs"><span className="text-xxs fg-muted">{labels[field]}{required ? ' *' : ''}</span><input className="input" value={customAddress[field]} onChange={event => setCustomAddress(current => ({ ...current, [field]: event.target.value }))} required={required} disabled={saving} /></label>;
                  })}
                </div>
              ) : null}
              {isInstallation && locationError ? <ErrorBanner message={errorMessage(locationError, 'Unable to load the installation address.')} /> : null}
              {isInstallation && selectedClient && !addressReady ? <span className="service-field-feedback" role="alert">Choose a site address with a street, city, and state, or enter an Other address.</span> : null}

              <div className="service-readonly-field service-readonly-field--address">
                <span className="text-xxs fg-muted">Selected address</span>
                {selectedClient ? (
                  <>
                    <strong>{locationDraft.branchName}</strong>
                    {locationLines.length ? locationLines.map((line) => <span key={line}>{line}</span>) : <span className="fg-muted">Address not set</span>}
                  </>
                ) : (
                  <span className="fg-muted">Select a customer to choose a location.</span>
                )}
              </div>

              <div className="service-contact-picker stack gap-xxs">
                <label className="text-xxs fg-muted" htmlFor="external-service-contact">{isInstallation ? 'Contact *' : 'Service contact *'}</label>
                <input
                  id="external-service-contact"
                  className="input"
                  role="combobox"
                  aria-expanded={contactOpen}
                  aria-controls="service-contact-options"
                  aria-invalid={showContactValidation ? true : undefined}
                  aria-describedby={showContactValidation ? 'external-service-contact-feedback' : undefined}
                  value={contactSearch}
                  onFocus={() => selectedClient && setContactOpen(true)}
                  onChange={(event) => handleContactInput(event.target.value)}
                  placeholder={selectedClient ? (contactsQuery.isFetching ? 'Loading contacts...' : 'Type to filter contacts') : 'Select a customer first'}
                  disabled={saving || !selectedClient}
                />
                {showContactValidation ? (
                  <span id="external-service-contact-feedback" className="service-field-feedback" role="alert">
                    {contactValidationMessage}
                  </span>
                ) : null}
                {contactOpen ? (
                  <div
                    id="service-contact-options"
                    className="service-contact-options stack gap-xxs"
                    role="listbox"
                  >
                    {contactsQuery.error ? (
                      <span className="text-xs fg-error service-contact-options__empty" role="alert">{errorMessage(contactsQuery.error, 'Unable to load customer contacts.')}</span>
                    ) : filteredContacts.length ? filteredContacts.map((contact) => (
                      <button
                        key={contact.id}
                        type="button"
                        className="btn subtle"
                        role="option"
                        aria-selected={selectedContactId === contact.id}
                        style={{ justifyContent: 'flex-start', textAlign: 'left' }}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => {
                          setSelectedContactId(contact.id);
                          setContactSearch(contactOptionLabel(contact));
                          setContactOpen(false);
                          setSubmitAttempted(false);
                          setError(null);
                        }}
                      >
                        <span className="stack gap-xxs">
                          <span>{clientContactDisplayName(contact)}</span>
                          <span className="text-xxs fg-muted">{[contact.title, contact.email, contact.phone].filter(Boolean).join(' / ') || 'No contact details'}</span>
                        </span>
                      </button>
                    )) : (
                      <span className="text-xs fg-muted service-contact-options__empty">No matching contacts.</span>
                    )}
                  </div>
                ) : null}
              </div>

              <div className="service-readonly-field service-readonly-field--compact">
                <span className="text-xxs fg-muted">Selected contact</span>
                <span className="text-sm">{selectedContact ? contactOptionLabel(selectedContact) : 'No contact selected'}</span>
              </div>
            </div>

            <div className="service-intake-column service-intake-column--workorder">
              <div className="service-intake-column__header">
                <h3 className="h5 m0">{isInstallation ? 'Installation' : 'Work Order'}</h3>
              </div>

              {isInstallation ? (
                <><label className="stack gap-xxs"><span className="text-xxs fg-muted">Quote type</span><select className="input" value={installationQuoteType} disabled={saving} onChange={event => setInstallationQuoteType(event.target.value as 'installation' | 'service_agreement')}><option value="installation">Installation</option><option value="service_agreement">Service agreement</option></select></label>
                <label className="stack gap-xxs"><span className="text-xxs fg-muted">{installationQuoteType === 'service_agreement' ? 'Quote name *' : 'Installation name *'}</span><input className="input" value={installationName} onChange={event => setInstallationName(event.target.value)} placeholder={installationQuoteType === 'service_agreement' ? 'Annual service coverage' : 'Executive conference room upgrade'} required disabled={saving} /></label></>
              ) : <div className="stack gap-xxs">
                <div className="service-problem-label-row">
                  <label className="text-xxs fg-muted" htmlFor="external-service-problem-reported">Problem reported *</label>
                  <button
                    type="button"
                    className={`service-specific-item-toggle${specificItemEnabled ? ' is-active' : ''}`}
                    aria-pressed={specificItemEnabled}
                    onClick={() => setSpecificItemEnabled((current) => !current)}
                    disabled={saving}
                  >
                    Specific item
                  </button>
                </div>
                <RichTextEditor
                  id="external-service-problem-reported"
                  label="Problem reported"
                  value={problem}
                  onChange={setProblem}
                  placeholder="Describe the customer-reported issue."
                  readOnly={saving}
                />
              </div>}

              {!isInstallation && specificItemEnabled ? (
                <div className="service-specific-item-grid">
                  <label className="stack gap-xxs service-specific-item-grid__wide">
                    <span className="text-xxs fg-muted">Item description</span>
                    <input
                      className="input"
                      value={specificItem.description}
                      onChange={(event) => setSpecificItem((prev) => ({ ...prev, description: event.target.value }))}
                      placeholder="Example: ceiling-mounted projector, ice machine, lift motor"
                      disabled={saving}
                    />
                  </label>
                  <label className="stack gap-xxs">
                    <span className="text-xxs fg-muted">Make</span>
                    <input
                      className="input"
                      value={specificItem.make}
                      onChange={(event) => setSpecificItem((prev) => ({ ...prev, make: event.target.value }))}
                      disabled={saving}
                    />
                  </label>
                  <label className="stack gap-xxs">
                    <span className="text-xxs fg-muted">Model</span>
                    <input
                      className="input"
                      value={specificItem.model}
                      onChange={(event) => setSpecificItem((prev) => ({ ...prev, model: event.target.value }))}
                      disabled={saving}
                    />
                  </label>
                  <label className="stack gap-xxs">
                    <span className="text-xxs fg-muted">Age</span>
                    <input
                      className="input"
                      value={specificItem.age}
                      onChange={(event) => setSpecificItem((prev) => ({ ...prev, age: event.target.value }))}
                      placeholder="Approximate"
                      disabled={saving}
                    />
                  </label>
                </div>
              ) : null}

              <div className="service-workorder-grid service-workorder-grid--timing">
                <label className="stack gap-xxs service-workorder-field service-workorder-field--date">
                  <span className="text-xxs fg-muted">{isInstallation ? 'Requested start date' : 'Requested service date'}</span>
                  <input className="input" type="date" value={requestedDate} onChange={(event) => handleRequestedDateChange(event.target.value)} disabled={saving} />
                </label>
                {!isInstallation ? <div className="stack gap-xxs service-workorder-field service-workorder-field--time">
                  <label className="text-xxs fg-muted" htmlFor="external-service-create-requested-time">Requested time</label>
                  <select
                    id="external-service-create-requested-time"
                    className="input"
                    value={requestedTimePreference}
                    onChange={(event) => handleRequestedTimePreferenceChange(event.target.value)}
                    disabled={saving}
                  >
                    {SERVICE_TICKET_TIME_PREFERENCE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  {requestedTimePreference === 'exact' ? (
                    <input
                      id="external-service-create-start-time"
                      className="input"
                      type="time"
                      value={requestedStartTime}
                      onChange={(event) => handleRequestedStartTimeChange(event.target.value)}
                      disabled={saving}
                      aria-label="Exact requested service time"
                    />
                  ) : null}
                </div> : null}
                {!isInstallation ? <label className="stack gap-xxs service-workorder-field service-workorder-field--priority">
                  <span className="text-xxs fg-muted">Priority</span>
                  <select className="input" value={priority} onChange={(event) => setPriority(event.target.value as ServiceTicketPriority)} disabled={saving}>
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </label> : null}
              </div>

              {!isInstallation && requestedTimePreference === 'exact' ? (
                <ExternalServiceSchedulePreview
                  weekAnchor={scheduleWeekAnchor}
                  requestedDate={requestedDate}
                  requestedStartTime={requestedStartTime}
                  serviceLocationName={serviceLocationName}
                  serviceLocationId={serviceLocationId}
                  serviceLocationLoading={serviceSettingsQuery.isFetching}
                  people={serviceLaborUsersQuery.data ?? []}
                  peopleLoading={serviceLaborUsersQuery.isFetching}
                  peopleError={serviceLaborUsersQuery.error ? errorMessage(serviceLaborUsersQuery.error, 'Unable to load service users.') : null}
                  shifts={serviceScheduleQuery.data ?? []}
                  shiftsLoading={serviceScheduleQuery.isFetching}
                  shiftsError={
                    serviceSettingsQuery.error
                      ? errorMessage(serviceSettingsQuery.error, 'Unable to load service location.')
                      : serviceScheduleQuery.error
                        ? errorMessage(serviceScheduleQuery.error, 'Unable to load service schedule.')
                        : null
                  }
                  onPreviousWeek={() => setScheduleWeekAnchor(toYmd(addDays(startOfServiceWeek(scheduleWeekAnchor), -7)))}
                  onToday={() => setScheduleWeekAnchor(todayYmd())}
                  onNextWeek={() => setScheduleWeekAnchor(toYmd(addDays(startOfServiceWeek(scheduleWeekAnchor), 7)))}
                  onSelectSlot={handleScheduleSlotSelect}
                />
              ) : null}
            </div>
          </div>

          {submitFeedbackMessage ? (
            <div
              className={`service-submit-feedback ${error ? 'service-submit-feedback--error' : 'service-submit-feedback--progress'}`}
              role={error ? 'alert' : 'status'}
              aria-live={error ? 'assertive' : 'polite'}
            >
              {saving ? <span className="service-submit-spinner" aria-hidden="true" /> : null}
              <span className="service-submit-feedback__body">
                <strong>{isInstallation ? (error ? 'Installation quote was not created' : 'Creating installation quote') : (error ? 'External service ticket was not created' : 'Creating external service ticket')}</strong>
                <span>{submitFeedbackMessage}</span>
              </span>
            </div>
          ) : null}

          {actions}
        </section>
      </form>

  );

  return (
    <>
      <div className="settings-shell settings-page">
        <div className="settings-page__pinned">
          <header className="settings-hero">
            <div className="settings-hero__headline">
              <span className="settings-hero__eyebrow">
                <Link to={isInstallation ? '/installations' : '/service'} className="link" style={{ textDecoration: 'none' }}>{isInstallation ? 'Installations' : 'Service'}</Link>
              </span>
              <h1 className="settings-card__title m0">{isInstallation ? 'Create Installation Quote' : 'New External Service Ticket'}</h1>
              <p className="settings-hero__body">{isInstallation ? 'Start an installation quote with the customer, site, and contact details.' : 'Create a customer-linked work order with service agreement and PM context.'}</p>
            </div>
          </header>
        </div>
        {intake}
      </div>
      {quickMode ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`Add ${quickMode}`}
          className="modal-overlay service-quick-modal-backdrop"
        >
          <div className="service-quick-modal">
            <div className="service-quick-modal__header">
              <h2 className="h4 m0">{quickModalTitle}</h2>
              <button type="button" className="btn sm subtle" onClick={() => setQuickMode(null)} disabled={quickSaving}>Cancel</button>
            </div>

            <div className="service-quick-modal__body">
              {quickError ? <ErrorBanner message={quickError} /> : null}

              {quickMode === 'customer' ? (
                <form id={quickFormId} className="service-quick-form" onSubmit={handleQuickCustomerSubmit}>
                  <label className="stack gap-xxs service-quick-form__wide">
                    <span className="text-xxs fg-muted">Customer name *</span>
                    <input className="input" value={quickCustomer.businessName} onChange={(event) => setQuickCustomer((prev) => ({ ...prev, businessName: event.target.value }))} required autoFocus />
                  </label>
                  <label className="stack gap-xxs service-quick-form__wide"><span className="text-xxs fg-muted">Address 1</span><input className="input" value={quickCustomer.addressLine1} onChange={(event) => setQuickCustomer((prev) => ({ ...prev, addressLine1: event.target.value }))} /></label>
                  <label className="stack gap-xxs service-quick-form__wide"><span className="text-xxs fg-muted">Address 2</span><input className="input" value={quickCustomer.addressLine2} onChange={(event) => setQuickCustomer((prev) => ({ ...prev, addressLine2: event.target.value }))} /></label>
                  <label className="stack gap-xxs"><span className="text-xxs fg-muted">City</span><input className="input" value={quickCustomer.city} onChange={(event) => setQuickCustomer((prev) => ({ ...prev, city: event.target.value }))} /></label>
                  <label className="stack gap-xxs"><span className="text-xxs fg-muted">State</span><input className="input" value={quickCustomer.state} onChange={(event) => setQuickCustomer((prev) => ({ ...prev, state: event.target.value }))} /></label>
                  <label className="stack gap-xxs"><span className="text-xxs fg-muted">Postal code</span><input className="input" value={quickCustomer.postalCode} onChange={(event) => setQuickCustomer((prev) => ({ ...prev, postalCode: event.target.value }))} /></label>
                  <label className="stack gap-xxs"><span className="text-xxs fg-muted">Country</span><input className="input" value={quickCustomer.country} onChange={(event) => setQuickCustomer((prev) => ({ ...prev, country: event.target.value }))} /></label>
                </form>
              ) : null}

              {quickMode === 'contact' ? (
                <form id={quickFormId} className="service-quick-form" onSubmit={handleQuickContactSubmit}>
                  <label className="stack gap-xxs"><span className="text-xxs fg-muted">First name</span><input className="input" value={quickContact.firstName} onChange={(event) => setQuickContact((prev) => ({ ...prev, firstName: event.target.value }))} autoFocus /></label>
                  <label className="stack gap-xxs"><span className="text-xxs fg-muted">Last name</span><input className="input" value={quickContact.lastName} onChange={(event) => setQuickContact((prev) => ({ ...prev, lastName: event.target.value }))} /></label>
                  <label className="stack gap-xxs"><span className="text-xxs fg-muted">Title</span><input className="input" value={quickContact.title} onChange={(event) => setQuickContact((prev) => ({ ...prev, title: event.target.value }))} /></label>
                  <label className="stack gap-xxs"><span className="text-xxs fg-muted">Email</span><input className="input" type="email" value={quickContact.email} onChange={(event) => setQuickContact((prev) => ({ ...prev, email: event.target.value }))} /></label>
                  <label className="stack gap-xxs service-quick-form__wide"><span className="text-xxs fg-muted">Phone</span><input className="input" value={quickContact.phone} onChange={(event) => setQuickContact((prev) => ({ ...prev, phone: event.target.value }))} /></label>
                </form>
              ) : null}

              {quickMode === 'location' ? (
                <form id={quickFormId} className="service-quick-form" onSubmit={handleQuickLocationSubmit}>
                  <label className="stack gap-xxs service-quick-form__wide">
                    <span className="text-xxs fg-muted">Location name *</span>
                    <input className="input" value={quickLocation.branchName} onChange={(event) => setQuickLocation((prev) => ({ ...prev, branchName: event.target.value }))} required autoFocus />
                  </label>
                  <label className="stack gap-xxs service-quick-form__wide"><span className="text-xxs fg-muted">Address 1</span><input className="input" value={quickLocation.addressLine1} onChange={(event) => setQuickLocation((prev) => ({ ...prev, addressLine1: event.target.value }))} /></label>
                  <label className="stack gap-xxs service-quick-form__wide"><span className="text-xxs fg-muted">Address 2</span><input className="input" value={quickLocation.addressLine2} onChange={(event) => setQuickLocation((prev) => ({ ...prev, addressLine2: event.target.value }))} /></label>
                  <label className="stack gap-xxs"><span className="text-xxs fg-muted">City</span><input className="input" value={quickLocation.city} onChange={(event) => setQuickLocation((prev) => ({ ...prev, city: event.target.value }))} /></label>
                  <label className="stack gap-xxs"><span className="text-xxs fg-muted">State</span><input className="input" value={quickLocation.state} onChange={(event) => setQuickLocation((prev) => ({ ...prev, state: event.target.value }))} /></label>
                  <label className="stack gap-xxs"><span className="text-xxs fg-muted">Postal code</span><input className="input" value={quickLocation.postalCode} onChange={(event) => setQuickLocation((prev) => ({ ...prev, postalCode: event.target.value }))} /></label>
                  <label className="stack gap-xxs"><span className="text-xxs fg-muted">Country</span><input className="input" value={quickLocation.country} onChange={(event) => setQuickLocation((prev) => ({ ...prev, country: event.target.value }))} /></label>
                </form>
              ) : null}
            </div>

            <div className="service-quick-modal__footer">
              <button type="button" className="btn" onClick={() => setQuickMode(null)} disabled={quickSaving}>Cancel</button>
              <button type="submit" form={quickFormId} className="btn btn-primary" disabled={quickSubmitDisabled}>{quickSubmitLabel}</button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
